import * as React from 'react';
import type {
  AnnotatedJsonSchema,
  EntityDataSourceType,
} from '@web-config-app/core';
import type {
  ConditionalActionConditionSubject,
  ValueCatalogMapping,
} from '../types';

export interface ConditionalActionSchemaMeta {
  titleKey: string;
  hintKey: string | undefined;
  /**
   * Locale key for the empty-state hint copy, read from
   * `x-entity-presentation.emptyStateHint.key`. Undefined when not declared.
   */
  emptyStateHintKey: string | undefined;
  /**
   * Locale key for the empty-state heading, read from
   * `x-entity-presentation.emptyStateTitle.key`. Falls back to the generic
   * `CONDITIONAL_ACTION_EMPTY_TITLE` when undeclared.
   */
  emptyStateTitleKey: string;
  rulesArrayAddLabel: string | undefined;
  /** Condition subject declarations (the subjects conditions test). */
  conditionSubjects: ConditionalActionConditionSubject[];
  /**
   * Locale key for the action verb pill in the THEN row. Read from the
   * configured action's `label.key` ("go to" for `goTo`, "show this
   * question" for `show`); falls back to the generic "go to" label when the
   * schema doesn't declare one.
   */
  actionLabelKey: string;
  /**
   * The single action this config offers: `goTo` (navigate) or `show`
   * (visibility). Drives whether the THEN row pairs the verb with a
   * destination picker (`goTo`) or a static verb pill (`show`), and whether
   * the default-destination field renders. Defaults to `goTo`.
   */
  actionType: 'goTo' | 'show';
  /**
   * Optional allow-list of operator IDs this schema instance offers.
   * `undefined` = no restriction (full runtime catalog).
   */
  allowedOperators: string[] | undefined;
  /** Optional per-instance operator label-key overrides, keyed by operator ID. */
  operatorLabelOverrides: Record<string, string> | undefined;
  /**
   * Whether a rule may hold more than one condition. `undefined`/`true` = the
   * control shows "add condition"; `false` hides it. v1 schema sets this
   * `false` (single-subject rules gain nothing from a second AND condition).
   */
  allowMultipleConditions: boolean | undefined;
  /** JSON Schema for one rule item (used by `createDefaultValueForSchema`). */
  ruleItemSchema: AnnotatedJsonSchema | undefined;
  /**
   * Name of the entity data source supplying the `goTo` action's target
   * (destination) picker options.
   */
  destinationDataSource: string | undefined;
  /**
   * Consumer type for the destination data source, from the `goTo`
   * action config's `type`. Defaults to `enumOptions` when undeclared.
   */
  destinationDataSourceType: EntityDataSourceType;
  /** Mapping for the destination picker's enum options. */
  destinationMapping: ValueCatalogMapping | undefined;
  defaultLabelKey: string;
  defaultHintKey: string;
}

interface DefaultFieldSchema {
  'x-entity-label'?: { key?: string };
  'x-entity-presentation'?: { hint?: { key?: string } };
}

/**
 * Centralizes the conditional-action control's schema reads so the control
 * and rule card don't do inline lookups or prop-drill mapping + data-source
 * name. Memoized on the `schema` reference (stable across control edits).
 */
export const useConditionalActionSchemaMeta = (
  schema: AnnotatedJsonSchema,
): ConditionalActionSchemaMeta =>
  React.useMemo(() => {
    const titleKey =
      schema['x-entity-label']?.key ?? 'CONDITIONAL_ACTION_LABEL';
    const hintKey = schema['x-entity-presentation']?.hint?.key;

    // `properties` is an open record; narrow to the fields we read. Accesses
    // are optional-chained, so a missing slot degrades to `undefined`.
    const rulesSchema = schema.properties?.rules as
      | {
          'x-entity-presentation'?: { arrayAddLabel?: string };
          items?: AnnotatedJsonSchema;
        }
      | undefined;
    const rulesArrayAddLabel =
      rulesSchema?.['x-entity-presentation']?.arrayAddLabel;

    const conditionalActionOptions =
      schema['x-entity-control']?.options?.conditionalAction;
    const conditionSubjects: ConditionalActionConditionSubject[] =
      conditionalActionOptions?.conditionSubjects ?? [];
    const emptyStateHintKey =
      schema['x-entity-presentation']?.emptyStateHint?.key;
    const emptyStateTitleKey =
      schema['x-entity-presentation']?.emptyStateTitle?.key ??
      'CONDITIONAL_ACTION_EMPTY_TITLE';

    // A control offers one action mode. `goTo` declares a destination
    // picker; `show` is payload-less (no picker, implicit hide default).
    // Defaults to `goTo`. See the multi-action TODO on
    // `ConditionalActionControlOptions.actions` — declaring both silently
    // drops `goTo`.
    const actions = conditionalActionOptions?.actions ?? {};
    const actionType: 'goTo' | 'show' = 'show' in actions ? 'show' : 'goTo';
    const goToAction = actions.goTo;
    const activeActionConfig =
      actionType === 'show' ? actions.show : goToAction;

    // Same open-record narrowing as `rulesSchema`.
    const defaultFieldSchema = schema.properties?.default as
      | DefaultFieldSchema
      | undefined;

    return {
      titleKey,
      hintKey,
      emptyStateHintKey,
      emptyStateTitleKey,
      rulesArrayAddLabel,
      conditionSubjects,
      actionType,
      actionLabelKey:
        activeActionConfig?.label?.key ?? 'CONDITIONAL_ACTION_GO_TO_LABEL',
      allowedOperators: conditionalActionOptions?.operators,
      operatorLabelOverrides: conditionalActionOptions?.labelOverrides,
      allowMultipleConditions:
        conditionalActionOptions?.allowMultipleConditions,
      ruleItemSchema: rulesSchema?.items,
      destinationDataSource: goToAction?.dataSource,
      destinationDataSourceType: goToAction?.type ?? 'enumOptions',
      destinationMapping: goToAction?.mapping,
      defaultLabelKey:
        defaultFieldSchema?.['x-entity-label']?.key ??
        'CONDITIONAL_ACTION_DEFAULT_LABEL',
      defaultHintKey:
        defaultFieldSchema?.['x-entity-presentation']?.hint?.key ??
        'CONDITIONAL_ACTION_DEFAULT_HINT',
    };
  }, [schema]);
